import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { DuplicateFileIcon } from '@/assets/icons';
import {
  ClusterIcon,
  DataRoomIcon,
  DeleteIcon,
  MoveToIcon,
  RedlineIcon,
  TagIcon,
} from '@/assets/images';
import { ROOT_FOLDER_PATH } from '@/common/constants';
import { DataRoomClientFile } from '@/common/types';
import { ContextMenu as ContextMenuComponent } from '@/components/ContextMenu';
import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { useOverview } from '@/pages/overview/common/utils';
import { buildFileMoves } from '@/pages/overview/dataroom/content/common/folderUtils';
import { showCopyToast, showMoveToast } from '@/pages/overview/dataroom/content/common/toast-utils';
import { createAndSelectRedlineFiles } from '@/pages/overview/redline/utils';
import { showErrorToast } from '@/utils/toasts/Toasts';
import { trpcClient, trpcReact } from '@/utils/trpc';

interface ContextMenuProps {
  focusedFile?: DataRoomClientFile;
  focusedFolder?: string;
  folderInteraction?: boolean;
  xPosition: number;
  yPosition: number;
}

export const ContextMenu = ({
  focusedFile,
  focusedFolder,
  folderInteraction,
  xPosition,
  yPosition,
}: ContextMenuProps) => {
  const featureFlags = useFlags();
  const { checkedFiles, updateSignature, foldersByPaths } = useDataRoom();

  // Mutation for move functionality
  const bulkMoveFiles = trpcReact.dataRoom.bulkMoveFilesToFolder.useMutation({
    onSuccess: (data, variables) => {
      const targetFolder = allFolders.find((folder) => folder.key === variables.targetFolderKey);
      const targetFolderName =
        targetFolder?.displayName || targetFolder?.name || 'destination folder';

      showMoveToast(data, variables.targetFolderKey, targetFolderName);
    },
    onError: (error) => {
      showErrorToast(`Failed to move files: ${error.message}`);
    },
  });
  const {
    matter: {
      number: matterNumber,
      name: matterName,
      client: { number: clientNumber, name: clientName },
      id: clientMatterId,
      redlines,
      folders: allFolders,
    },
    isUserCurrentEditor,
  } = useOverview();
  const navigate = useNavigate();

  const folderName = focusedFolder?.split('/').pop() ?? '';

  // Mutation for copy functionality (linked files feature)
  const bulkCopyFiles = trpcReact.dataRoom.bulkCopyFilesToFolder.useMutation({
    onSuccess: (data, variables) => {
      const targetFolder = allFolders.find((folder) => folder.key === variables.targetFolderKey);
      const targetFolderName =
        targetFolder?.displayName || targetFolder?.name || 'destination folder';

      showCopyToast(data, variables.targetFolderKey, targetFolderName, variables.fileNames.length);
    },
    onError: (error) => {
      showErrorToast(`Failed to copy files: ${error.message}`);
    },
  });

  const onPathSelect = useCallback(
    (selectedArea: string) => {
      const filesToMove: DataRoomClientFile[] = [];

      if (checkedFiles.some((file) => file.name === focusedFile?.name)) {
        checkedFiles.forEach((file) => {
          filesToMove.push(file);
        });
      }
      if (focusedFile) {
        filesToMove.push(focusedFile);
      }

      if (filesToMove.length > 0) {
        const targetFolder = allFolders.find((folder) => folder.key === selectedArea);
        if (!targetFolder) return;

        // Build fileMoves with best-effort source context
        const fileMoves = buildFileMoves(filesToMove, foldersByPaths);

        bulkMoveFiles.mutate({
          clientMatterId,
          fileMoves,
          targetFolderKey: targetFolder.key,
          targetFolderType: 'default',
        });
      }
    },
    [checkedFiles, clientMatterId, focusedFile, allFolders, bulkMoveFiles, foldersByPaths],
  );

  const onFolderMove = useCallback(
    (folderTarget: string) => {
      if (!focusedFolder) return;
      const targetFolder = foldersByPaths.get(folderTarget)?.[0];
      if (!targetFolder) return;

      trpcClient.dataRoom.updateFolderParent.mutate({
        clientMatterId,
        folderKey: focusedFolder.split('/')[1],
        newParentKey: targetFolder.key,
      });
    },
    [clientMatterId, focusedFolder, foldersByPaths],
  );

  const createRedlineAndNavigate = useCallback(
    async (mode: 'clustering' | 'diff') => {
      if (checkedFiles.length === 0) return;
      const redlineName = `Redline ${redlines.length + 1}`;
      await createAndSelectRedlineFiles(
        checkedFiles,
        matterNumber,
        clientNumber,
        clientMatterId,
        redlineName,
      );
      navigate(`../redline/${redlineName}/${mode}`);
    },
    [checkedFiles, redlines.length, matterNumber, clientNumber, clientMatterId, navigate],
  );

  const onCluster = useCallback(
    () => createRedlineAndNavigate('clustering'),
    [createRedlineAndNavigate],
  );
  const onRedline = useCallback(() => createRedlineAndNavigate('diff'), [createRedlineAndNavigate]);

  const onAddMissingSignatureTag = useCallback(() => {
    if (focusedFile) {
      updateSignature(focusedFile.name, true);
    }
  }, [focusedFile, updateSignature]);

  // Copy functionality for linked files feature
  const onPathCopy = useCallback(
    (selectedFolderKey: string) => {
      const fileNames: string[] = [];
      if (checkedFiles.some((file) => file.name === focusedFile?.name)) {
        checkedFiles.forEach((file) => {
          fileNames.push(file.name);
        });
      }
      if (focusedFile) {
        fileNames.push(focusedFile.name);
      }

      const targetFolder = allFolders.find((folder) => folder.key === selectedFolderKey);
      if (targetFolder && fileNames.length > 0) {
        bulkCopyFiles.mutate({
          clientMatterId,
          fileNames: [...new Set(fileNames)], // Remove duplicates
          targetFolderKey: targetFolder.key,
          targetFolderType: targetFolder.type || 'default',
        });
      }
    },
    [checkedFiles, clientMatterId, focusedFile, allFolders, bulkCopyFiles],
  );

  const handleOptionClick = useCallback(
    (option: string) => {
      if (folderInteraction) {
        onFolderMove(option);
      } else {
        onPathSelect(option);
      }
    },
    [folderInteraction, onFolderMove, onPathSelect],
  );

  const getFolderOptions = useCallback(() => {
    const options = [];

    // Always add root option for move operations
    const rootFolder = foldersByPaths.get(ROOT_FOLDER_PATH)?.[0];
    if (rootFolder) {
      options.push({
        title: !matterName ? clientName || 'Document Set' : 'Document Set',
        icon: DataRoomIcon,
        onClick: () => handleOptionClick(rootFolder.key),
      });
    }

    // Add other folders
    const folderOptions = Array.from(foldersByPaths.values())
      .flatMap((folders) => folders)
      .filter((folder) => {
        if (!folder) return false;
        if (folder.key === 'root') return false;
        if (folder.key === '~trash') return false;
        if (folderInteraction && focusedFolder) {
          return !folder.key.startsWith(focusedFolder.split('/')[1]);
        }
        return true;
      })
      .map((folder) => {
        const title = folder.displayName || folder.name || folder.key || 'Unnamed Folder';
        return {
          title,
          icon: MoveToIcon,
          onClick: () => handleOptionClick(folder.key),
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));

    options.push(...folderOptions);

    // Add trash option
    const trashFolder = foldersByPaths.get('/root/~trash')?.[0];
    if (trashFolder) {
      options.push({
        title: 'Trash',
        icon: DeleteIcon,
        onClick: () => handleOptionClick(trashFolder.key),
      });
    }

    return options;
  }, [clientName, focusedFolder, folderInteraction, foldersByPaths, handleOptionClick, matterName]);

  // Folder options for copy operations (linked files feature)
  const getCopyFolderOptions = useCallback(() => {
    const options = [];

    // Always add root option for copy operations (allows creating linked files)
    const rootFolder = foldersByPaths.get(ROOT_FOLDER_PATH)?.[0];
    if (rootFolder) {
      options.push({
        title: !matterName ? clientName || 'Document Set' : 'Document Set',
        icon: DataRoomIcon,
        onClick: () => onPathCopy(rootFolder.key),
        disabled: bulkCopyFiles.isLoading,
      });
    }

    // Add other folders (similar to move functionality but for copy)
    const folderOptions = Array.from(foldersByPaths.values())
      .flatMap((folders) => folders)
      .filter((folder) => {
        if (!folder) return false;
        if (folder.key === 'root') return false;
        if (folder.key === '~trash') return false; // Skip trash for copy operations
        // Allow copying to any folder, even current one (creates linked files)
        return true;
      })
      .map((folder) => {
        const title = folder.displayName || folder.name || folder.key || 'Unnamed Folder';
        return {
          title,
          icon: MoveToIcon,
          onClick: () => onPathCopy(folder.key),
          disabled: bulkCopyFiles.isLoading,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));

    options.push(...folderOptions);

    return options;
  }, [clientName, foldersByPaths, matterName, onPathCopy, bulkCopyFiles.isLoading]);

  const contextMenuTitle = () => {
    if (folderInteraction) {
      return folderName;
    }
    if (checkedFiles) {
      if (checkedFiles.some((file) => file.name === focusedFile?.name)) {
        if (checkedFiles.length === 1) {
          return `${checkedFiles.length} file selected`;
        }
        return `${checkedFiles.length} files selected`;
      } else {
        return focusedFile?.displayName;
      }
    }
  };

  const getMenuOptions = useCallback(() => {
    const baseOptions = [
      {
        title: 'Move To',
        icon: MoveToIcon,
        options: getFolderOptions(),
      },
    ];

    // Add "Copy to" option if linked files feature is enabled
    if (featureFlags.linkedFiles && !folderInteraction) {
      baseOptions.push({
        title: 'Copy To',
        icon: DuplicateFileIcon,
        options: getCopyFolderOptions(),
      });
    }

    if (!folderInteraction) {
      baseOptions.push({
        title: 'Add "Missing Signature(s)" tag',
        icon: TagIcon,
        options: [
          {
            title: 'Add "Missing Signature(s)" tag',
            icon: TagIcon,
            onClick: onAddMissingSignatureTag,
          },
        ],
      });
    }

    if (featureFlags.redlineFeature && checkedFiles && checkedFiles.length > 1) {
      baseOptions.push({
        title: 'Redline',
        icon: RedlineIcon,
        options: [
          {
            title: 'Run a Redline',
            icon: RedlineIcon,
            onClick: onRedline,
          },
          {
            title: 'Cluster',
            icon: ClusterIcon,
            onClick: onCluster,
          },
        ],
      });
    }

    return baseOptions;
  }, [
    folderInteraction,
    getFolderOptions,
    getCopyFolderOptions,
    onAddMissingSignatureTag,
    featureFlags.redlineFeature,
    featureFlags.linkedFiles,
    checkedFiles,
    onRedline,
    onCluster,
  ]);

  return (
    <ContextMenuComponent
      title={contextMenuTitle()}
      disabled={!isUserCurrentEditor}
      options={getMenuOptions()}
      x={xPosition}
      y={yPosition}
      data-testid="context-menu-component"
    />
  );
};
